import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Clock, Trash, Receipt } from "@phosphor-icons/react";
import { fmt } from "@/lib/format";

const timeAgo = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
};

export default function OpenTicketsDialog({ open, onClose, onSelect, refreshKey }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders/open");
      setOrders(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open, refreshKey]);

  const discard = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Discard this open ticket?")) return;
    await api.delete(`/orders/${id}/open`);
    load();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="open-tickets-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Receipt size={24} weight="duotone" /> Open Tickets
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto scroll-thin">
          {loading && <div className="text-center py-8 text-stone-500">Loading…</div>}
          {!loading && orders.length === 0 && (
            <div className="text-center py-12 text-stone-500">
              <div className="text-5xl mb-3">🎫</div>
              <div className="text-sm">No open tickets</div>
              <div className="text-xs mt-1">Save a KOT to keep an order open</div>
            </div>
          )}
          <div className="space-y-2">
            {orders.map((o) => (
              <button
                key={o.id}
                onClick={() => { onSelect(o); onClose(); }}
                className="w-full text-left border border-boba-stoneLine rounded-md p-4 hover:border-boba-matcha hover:bg-boba-matcha/5 transition-colors"
                data-testid={`open-ticket-${o.order_number}`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-mono font-bold text-lg text-boba-matcha">#{o.order_number}</div>
                    <div className="text-xs text-stone-500 mt-0.5">by {o.staff_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-semibold">{fmt(o.subtotal)}</div>
                    <div className="text-xs text-stone-500 flex items-center gap-1 justify-end mt-0.5">
                      <Clock size={12} /> {timeAgo(o.created_at)}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-end mt-3">
                  <div className="text-sm text-stone-700">
                    {o.items.length} item{o.items.length !== 1 ? "s" : ""} ·
                    <span className="ml-1 text-stone-500">{o.kot_batches?.length || 0} KOT{(o.kot_batches?.length || 0) !== 1 ? "s" : ""}</span>
                  </div>
                  <span
                    onClick={(e) => discard(o.id, e)}
                    className="text-boba-danger hover:bg-red-50 p-1.5 rounded text-xs flex items-center gap-1"
                    data-testid={`discard-ticket-${o.order_number}`}
                  >
                    <Trash size={14} /> Discard
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
